/**
 * 옵션 매핑 설정
 *
 * CartModal의 parseCustomizationsToOptions 함수에서 사용하는
 * 다국어 옵션 매핑 데이터를 별도 파일로 추출한 것입니다.
 *
 * 지원 언어: 한국어, 영어, 스페인어
 */

/**
 * 카테고리명 → 옵션 그룹 ID 매핑 (다국어 지원)
 *
 * 장바구니에 저장된 커스터마이제이션 문자열에서
 * 카테고리명(예: "고기 종류", "Size")을 그룹 ID(예: "mainChoice")로 변환합니다.
 */
export const CATEGORY_TO_GROUP_ID: Record<string, string> = {
  // 한국어
  '고기 종류': 'mainChoice',
  '메인 선택': 'mainChoice',
  '크기': 'size',
  '사이즈': 'size',
  '매운 정도': 'spiceLevel',
  '토핑 추가': 'toppings',
  '토핑': 'toppings',
  '사이드': 'sideChoice',
  '사이드 선택': 'sideChoice',
  '도우': 'dough',
  '치즈': 'cheese',
  '온도': 'temperature',
  '소스': 'sauce',
  '추가 옵션': 'extras',
  // 스페인어
  'Tipo de carne': 'mainChoice',
  'Selección principal': 'mainChoice',
  'Tamaño': 'size',
  'Nivel de picante': 'spiceLevel',
  'Ingredientes': 'toppings',
  'Acompañamiento': 'sideChoice',
  'Masa': 'dough',
  'Queso': 'cheese',
  'Temperatura': 'temperature',
  'Salsa': 'sauce',
  // 영어
  'Meat type': 'mainChoice',
  'Main choice': 'mainChoice',
  'Size': 'size',
  'Spice level': 'spiceLevel',
  'Toppings': 'toppings',
  'Side': 'sideChoice',
  'Dough': 'dough',
  'Cheese': 'cheese',
  'Temperature': 'temperature',
  'Sauce': 'sauce',
};

/**
 * 옵션명 → 옵션 ID 매핑 (다국어 지원)
 *
 * 장바구니에 저장된 커스터마이제이션 문자열에서
 * 옵션명(예: "소고기", "Beef")을 옵션 ID(예: "beef")로 변환합니다.
 */
export const OPTION_NAME_TO_ID: Record<string, string> = {
  // 한국어 - 고기
  '소고기': 'beef',
  '닭고기': 'chicken',
  '돼지고기': 'pork',
  '연어': 'salmon',
  '참치': 'tuna',
  '새우': 'shrimp',
  // 스페인어 - 고기 (Carne)
  'Carne de res': 'beef',
  'Pollo': 'chicken',
  'Cerdo': 'pork',
  'Salmón': 'salmon',
  'Atún': 'tuna',
  'Camarones': 'shrimp',
  // 영어 - 고기 (Meat)
  'Beef': 'beef',
  'Chicken': 'chicken',
  'Pork': 'pork',
  'Salmon': 'salmon',
  'Tuna': 'tuna',
  'Shrimp': 'shrimp',
  // 한국어 - 사이즈
  '라지': 'large',
  '큰 사이즈': 'large',
  '미디움': 'medium',
  '중간': 'medium',
  '스몰': 'small',
  '작은 사이즈': 'small',
  // 스페인어 - 사이즈 (Tamaño)
  'Grande': 'large',
  'Mediano': 'medium',
  'Pequeño': 'small',
  // 영어 - 사이즈 (Size)
  'Large': 'large',
  'Medium': 'medium',
  'Small': 'small',
  // 한국어 - 매운 정도
  '매운맛': 'spicy',
  '보통': 'medium_spice',
  '순한맛': 'mild',
  '안 맵게': 'mild',
  // 스페인어 - 매운 정도 (Nivel de picante)
  'Picante': 'spicy',
  'Medio': 'medium_spice',
  'Suave': 'mild',
  // 영어 - 매운 정도 (Spice level)
  'Spicy': 'spicy',
  'Mild': 'mild',
  // 한국어 - 토핑
  '치즈 추가': 'extraCheese',
  '올리브 추가': 'olives',
  '할라피뇨 추가': 'jalapenos',
  '양파 추가': 'onions',
  '버섯 추가': 'mushrooms',
  // 스페인어 - 토핑 (Ingredientes)
  'Queso extra': 'extraCheese',
  'Aceitunas': 'olives',
  'Jalapeños': 'jalapenos',
  'Cebollas': 'onions',
  'Champiñones': 'mushrooms',
  // 영어 - 토핑 (Toppings)
  'Extra cheese': 'extraCheese',
  'Olives': 'olives',
  'Onions': 'onions',
  'Mushrooms': 'mushrooms',
  // 한국어 - 사이드
  '과카몰리': 'guacamole',
  '살사 소스': 'salsa',
  '사워크림': 'sourCream',
  '감자튀김': 'fries',
  // 스페인어 - 사이드 (Acompañamiento)
  'Guacamole': 'guacamole',
  'Salsa': 'salsa',
  'Crema agria': 'sourCream',
  'Papas fritas': 'fries',
  // 영어 - 사이드 (Side)
  'Sour cream': 'sourCream',
  'Fries': 'fries',
  // 한국어 - 도우
  '씬 크러스트': 'thin',
  '얇은 도우': 'thin',
  '두꺼운 크러스트': 'thick',
  '두꺼운 도우': 'thick',
  '일반': 'regular',
  // 스페인어 - 도우 (Masa)
  'Masa delgada': 'thin',
  'Masa gruesa': 'thick',
  'Regular': 'regular',
  // 영어 - 도우 (Dough)
  'Thin crust': 'thin',
  'Thick crust': 'thick',
  // 한국어 - 치즈
  '추가 모짜렐라': 'extraMozzarella',
  '체다 치즈': 'cheddar',
  '파마산': 'parmesan',
  // 스페인어 - 치즈 (Queso)
  'Mozzarella extra': 'extraMozzarella',
  'Queso cheddar': 'cheddar',
  'Parmesano': 'parmesan',
  // 영어 - 치즈 (Cheese)
  'Extra mozzarella': 'extraMozzarella',
  'Cheddar cheese': 'cheddar',
  'Parmesan': 'parmesan',
  // 한국어 - 온도
  '뜨겁게': 'hot',
  '따뜻하게': 'warm',
  '차갑게': 'cold',
  // 스페인어 - 온도 (Temperatura)
  'Caliente': 'hot',
  'Tibio': 'warm',
  'Frío': 'cold',
  // 영어 - 온도 (Temperature)
  'Hot': 'hot',
  'Warm': 'warm',
  'Cold': 'cold',
};

/**
 * 그룹 추론에 사용되는 키워드 매핑
 *
 * 옵션명에서 카테고리가 명시되지 않은 경우,
 * 옵션명에 포함된 키워드를 기반으로 그룹 ID를 추론합니다.
 */
export const GROUP_INFERENCE_KEYWORDS = {
  // 메인 선택 (고기)
  mainChoice: [
    '소고기', '닭고기', '돼지고기', '연어', '참치', '새우',
    'beef', 'chicken', 'pork', 'salmon', 'tuna', 'shrimp',
    'Carne de res', 'Pollo', 'Cerdo', 'Salmón', 'Atún', 'Camarones',
  ],
  // 사이즈
  size: [
    '라지', '큰 사이즈', '미디움', '중간', '스몰', '작은 사이즈',
    'large', 'medium', 'small',
    'Grande', 'Mediano', 'Pequeño',
  ],
  // 매운 정도
  spiceLevel: [
    '매운맛', '보통', '순한맛', '안 맵게',
    'spicy', 'mild',
    'Picante', 'Medio', 'Suave',
  ],
} as const;

/**
 * 타입 정의: 그룹 추론 키워드 타입
 */
export type GroupInferenceGroup = keyof typeof GROUP_INFERENCE_KEYWORDS;
